import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import superAdminsService from '@/services/super-admins.service';
import type { SORT_ORDER, SuperAdminCreateRequest } from '@/types';

export type SuperAdminListParams = {
  cursor?: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: SORT_ORDER;
};

export const useSuperAdmins = (params: SuperAdminListParams = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SUPER_ADMINS, params],
    queryFn: () => superAdminsService.list(params),
  });
};

export const useSuperAdminById = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SUPER_ADMINS, id],
    queryFn: () => superAdminsService.getById(id ?? ''),
    enabled: !!id,
  });
};

export const useCreateSuperAdminMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SuperAdminCreateRequest) => superAdminsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUPER_ADMINS] });
      showNotification({ title: 'Success', message: 'Super admin created successfully', color: 'green' });
    },
  });
};

export const useUpdateSuperAdminMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SuperAdminCreateRequest> }) =>
      superAdminsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUPER_ADMINS] });
      showNotification({ title: 'Success', message: 'Super admin updated successfully', color: 'green' });
    },
  });
};

export const useRemoveSuperAdminMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => superAdminsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUPER_ADMINS] });
      showNotification({ title: 'Success', message: 'Super admin removed successfully', color: 'green' });
    },
  });
};
